import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CustomUser } from 'api/models/CustomUser';
import { ApiService } from 'api/services/ApiService';
import { useLocalStorage } from 'services/hooks';
import { ModelView, ModelViewsSelectorProps } from './ModelViewsSelector.props';
import { MainContainer, ViewButtonContainer, ViewsContainer } from './ModelViewsSelector.styles';
import ViewButtonWithTooltip from './ViewButtonWithTooltip';
import { ACTIVE_MODEL_VIEW_KEY, MODELS_VIEWS_KEY } from '../common/constants';

const ModelViewsSelector = ({ onLoadView, currentViewId, views: externalViews }: ModelViewsSelectorProps) => {
  const [internalViews] = useLocalStorage<ModelView[]>(MODELS_VIEWS_KEY, []);
  const [, setActiveModelView] = useLocalStorage<ModelView | null>(ACTIVE_MODEL_VIEW_KEY, null);
  const views = externalViews ?? internalViews;

  // Reuse the cached staff users query (same key as useModelFilters)
  const { data: staffUsers = [] } = useQuery<CustomUser[]>({
    queryKey: ['users-list', 'staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList();
      return (response.filter(user => !user.email?.includes('+')) as CustomUser[]);
    },
  });

  const handleLoadView = useCallback((event: React.MouseEvent<HTMLButtonElement>, savedView: ModelView) => {
    event.stopPropagation();
    onLoadView(savedView);
    setActiveModelView(savedView);
  }, [onLoadView, setActiveModelView]);

  return (
    <MainContainer sx={{ ml: 1 }}>
      <ViewsContainer>
        {views.map(savedView => (
          <ViewButtonContainer key={savedView.id}>
            <ViewButtonWithTooltip
              savedView={savedView}
              currentViewId={currentViewId}
              onClick={handleLoadView}
              staffUsers={staffUsers}
            />
          </ViewButtonContainer>
        ))}
      </ViewsContainer>
    </MainContainer>
  );
};

export default ModelViewsSelector;